<template>
  <v-ons-page class="ja-content-confirm page">
    <common-header>
      <template #header>
        <span>コンテンツの確認</span>
      </template>
    </common-header>
    <div class="content-confirm-area">
      <ja-content-preview
        class="preview-area"
        :processNum="CREATE_CONTENT_PROCESS_NUM.CONTENT_CONFIRM"
        :templateId="templateId"
        :contentMaterialList="contentMaterialList"
        :contentCaption="contentCaption"
        :contentCaptionColor="contentCaptionColor"
        :clickMaterial="clickMaterial"
        :isEditing="true"
        :contentFrame="contentFrame"
        :isAudioButtonVisible="true"
      />
      <p class="preview-description">調整したい領域をタップしてください</p>
      <div class="content-element-area">
        <content-preview-button
          class="content-preview-button"
          @click="showIsFullScreenPreviewMode"
        />
      </div>
      <!-- フレーム領域 -->
      <div class="content-element-area" v-if="HORIZONTAL_TEMPLATES[templateId]">
        <ja-content-frame
          :initialSelectedContentFrame="contentFrame"
          :showMoreFrame="showMoreFrame"
          :isEditing="true"
        />
      </div>
      <!-- テロップ領域 -->
      <div class="content-element-area">
        <v-ons-row>
          <v-ons-col><label>テロップ</label></v-ons-col>
        </v-ons-row>
        <!-- テロップ編集領域 -->
        <p class="content-caption-area">
          <label class="edit-caption-label" for="edit-caption" />
          <textarea
            type="text"
            name="edit-caption"
            class="text-area"
            id="edit-caption"
            v-model="contentCaption"
            placeholder="テロップを入れて、文字色を選択してください (未入力可)"
            maxlength="200"
          />
        </p>
        <!-- テロップ文字数 -->
        <div>
          <v-ons-col class="caption-count">
            <p>{{ calcLength() }}/200</p>
          </v-ons-col>
        </div>
        <!-- 文字色選択領域 -->
        <div class="ja-content-caption-color-area">
          <button
            v-for="(color, key) in CONTENT_CAPTION_COLOR"
            :key="key"
            :class="[
              'button ja-content-caption-color-cell',
              { isSelected: color === contentCaptionColor },
            ]"
            :style="{ backgroundColor: color }"
            @click="selectContentCaptionColor(color)"
          ></button>
        </div>
      </div>
      <!-- テロップ読み上げ領域 -->
      <div class="content-element-area">
        <label>テロップの読み上げ</label>
        <toggle-switch :value="isCaptionSpeech" @input="changeIsCaptionSpeech" />
      </div>
      <div class="content-element-area">
        <label>テンプレート</label>
        <div class="template-text-area">
          <p class="template-title">{{ TEMPLATES[templateId].TITLE }}</p>
          <p class="template-description">{{ TEMPLATES[templateId].DESCRIPTION }}</p>
        </div>
      </div>
    </div>
    <v-ons-modal
      :visible="isFullScreenPreviewMode"
      id="content-preview-full-screen-modal"
      class="modal-wrapper p-fixed"
    >
      <button class="modal-close-mask" @click="showIsFullScreenPreviewMode" />
      <div class="modal-close-button-area">
        <Button class="modal-close-button" @click="showIsFullScreenPreviewMode">
          <v-ons-icon class="modal-close-icon" icon="fa-close" />
        </Button>
      </div>
      <ja-content-preview
        class="preview-area"
        :processNum="CREATE_CONTENT_PROCESS_NUM.CONTENT_CONFIRM"
        :isFullScreenPreviewMode="true"
        :isVisible="isFullScreenPreviewMode"
        :templateId="templateId"
        :contentMaterialList="contentMaterialList"
        :contentCaption="contentCaption"
        :contentCaptionColor="contentCaptionColor"
        :timestamp="timestamp"
        :isEditing="false"
        :contentFrame="contentFrame"
      />
    </v-ons-modal>
    <div class="canvas">
      <canvas id="canvas" width="0" height="0"></canvas>
    </div>
    <common-footer class="common-footer d-flex">
      <template #footer>
        <v-ons-row class="footer-button-area">
          <small-button
            class="footer-back-button"
            title="戻る"
            :disabled="false"
            @click="() => moveBackButton(router)"
          />
          <small-button
            class="footer-upload-button"
            title="保存する"
            @click="contentUpload"
            :disabled="isAxiosWait"
          />
        </v-ons-row>
      </template>
    </common-footer>
  </v-ons-page>
</template>

<script setup>
import { ref, computed, onMounted, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import CommonHeader from '@/components/common/CommonHeader.vue';
import CommonFooter from '@/components/common/CommonFooter.vue';
import ToggleSwitch from '@/components/common/ToggleSwitch.vue';
import SmallButton from '@/components/common/SmallButton.vue';
import ContentPreviewButton from '@/components/common/ContentPreviewButton.vue';
import JaContentPreview from '@/components/JaContent/JaContentPreview.vue';
import JaContentFrame from '@/components/JaContent/JaContentFrame.vue';
import axios from '@/composable/axios';
import {
  disableScroll,
  enableScroll,
  createUuid,
  handleAxiosCommonException,
  moveBackButton,
} from '@/composable/common';
import { createContentCaptionPng } from '@/composable/content_common';
import {
  CONTENT_CAPTION_COLOR,
  CREATE_CONTENT_PROCESS_NUM,
  HORIZONTAL_TEMPLATES,
  TEMPLATES,
} from '@/composable/constant_common';
import {
  uploadContentCaptionPngBlobStorage,
  deleteContentCaptionPngBlobStorage,
} from '@/composable/externalServiceCommon';
import Button from '@/components/common/Button.vue';

const store = useStore();
const router = useRouter();

// axios通信処理待機フラグ
const isAxiosWait = ref(false);
// プレビュー可視状態
const isFullScreenPreviewMode = ref(false);
// 全画面プレビューでGIFアニメを表示するためのタイムスタンプ
const timestamp = ref(0);
// テロップ文言
const contentCaption = ref('');
// テロップ文字色
const contentCaptionColor = ref('');
// テロップ読み上げ要否
const isCaptionSpeech = ref(false);
// テロップ画像化用のキャンバス
const canvas = ref('');

// テンプレートID
const templateId = computed(() => store.getters['content/getTemplateId']);
// コンテンツのマテリアル情報
const contentMaterialList = computed(() => store.getters['content/getContentMaterialList']);
// コンテンツのフレーム情報取得
const contentFrame = computed(() => store.getters['content/getContentFrame']);

watch(isFullScreenPreviewMode, (newValue) => {
  if (newValue) {
    disableScroll();
  } else {
    enableScroll();
  }
});

/**
 * コンテンツの登録処理
 * @param contentCaptionPngUrl テロップ画像URL
 */
const registerContent = async (contentCaptionPngUrl) => {
  const ContentParameters = new FormData();
  ContentParameters.append('templateId', templateId.value);
  ContentParameters.append('contentCaption', contentCaption.value);
  ContentParameters.append('contentCaptionPngUrl', contentCaptionPngUrl);
  ContentParameters.append('contentCaptionColor', contentCaptionColor.value);
  ContentParameters.append('materialIdList', store.getters['content/getMaterialIdList']);
  ContentParameters.append('userId', store.getters['userInformation/getUserId']);
  ContentParameters.append(
    'isCaptionSpeech',
    Number(isCaptionSpeech.value && !!contentCaption.value)
  );

  // マテリアル情報をパラメータに追加
  const contentMaterialListFormData = store.getters['content/getContentMaterialList'];
  const materialIdListFormData = store.getters['content/getMaterialIdList'];
  for (let i = 0; i < materialIdListFormData.length; i += 1) {
    ContentParameters.append(
      `contentInformationList[${i}].offsetX`,
      contentMaterialListFormData[i].offsetX
    );
    ContentParameters.append(
      `contentInformationList[${i}].offsetY`,
      contentMaterialListFormData[i].offsetY
    );
    ContentParameters.append(
      `contentInformationList[${i}].scale`,
      contentMaterialListFormData[i].scale
    );
    ContentParameters.append(
      `contentInformationList[${i}].isAudioEnabled`,
      Number(contentMaterialListFormData[i].isAudioEnabled)
    );
  }

  // フレーム情報をパラメータに追加
  const contentFrameData = store.getters['content/getContentFrame'];
  ContentParameters.append('frameId', contentFrameData.frameId);

  // コンテンツ登録API呼び出し
  await axios.post('/content', ContentParameters, {
    headers: { Authorization: localStorage.getItem('token') },
  });
  // コンテンツ登録成功時、コンテンツ管理画面へ遷移
  router.push('/ja/content');
};

/**
 * コンテンツアップロード処理
 */
const contentUpload = async () => {
  if (isAxiosWait.value) return; // 連打防止
  isAxiosWait.value = true;
  let contentCaptionPngUrl = '';
  try {
    // テロップ入力時のみテロップ画像作成
    if (contentCaption.value) {
      const contentCaptionPng = await createContentCaptionPng(
        canvas.value,
        contentCaption.value,
        contentCaptionColor.value
      );
      // テロップ画像ファイル名作成(uuid)
      contentCaptionPngUrl = `${createUuid()}.png`;
      // テロップ画像をBlobストレージにアップロード
      await uploadContentCaptionPngBlobStorage(contentCaptionPng, contentCaptionPngUrl);
    }
    // コンテンツ登録処理呼び出し
    await registerContent(contentCaptionPngUrl);
  } catch (error) {
    if (contentCaption.value) {
      deleteContentCaptionPngBlobStorage(contentCaptionPngUrl);
    }
    // Axios共通エラーハンドリング処理をCall
    handleAxiosCommonException(error);
  } finally {
    isAxiosWait.value = false;
  }
};

/**
 * 改行の削除処理
 */
const removeLine = () => {
  contentCaption.value = contentCaption.value.replace('\n', '');
};

/**
 * 全画面プレビュー表示処理
 */
const showIsFullScreenPreviewMode = () => {
  removeLine();
  timestamp.value = new Date().getTime();
  isFullScreenPreviewMode.value = !isFullScreenPreviewMode.value;
};

/**
 * 素材選択処理
 */
const clickMaterial = (materialNum) => {
  removeLine();
  store.commit('content/setContentCaption', contentCaption.value);
  store.commit('content/setContentCaptionColor', contentCaptionColor.value);
  router.push({
    name: 'ja-content-image-resize',
    params: { materialNum },
  });
};

/**
 * もっと見るボタン押下処理
 */
const showMoreFrame = () => {
  removeLine();
  store.commit('content/setContentCaption', contentCaption.value);
  store.commit('content/setContentCaptionColor', contentCaptionColor.value);
  router.push('/ja/content/frameSelect');
};

/**
 * 入力値から改行を削除して入力文字数を返す処理
 */
const calcLength = () => {
  removeLine();
  return contentCaption.value.length;
};

/**
 * 読み上げtrueにする&テロップ未入力の場合は読み上げをfalseのままにする
 * それ以外は引数のデータで読み上げ要否変数を更新する
 */
const changeIsCaptionSpeech = (isCaptionSpeechValue) => {
  if (isCaptionSpeechValue && contentCaption.value.length === 0) return;
  isCaptionSpeech.value = isCaptionSpeechValue;
};

/**
 * テロップ文字色をセットする処理
 */
const selectContentCaptionColor = (color) => {
  contentCaptionColor.value = color;
  store.commit('content/setContentCaptionColor', contentCaptionColor.value);
};

onMounted(() => {
  contentCaption.value = store.getters['content/getContentCaption'];
  contentCaptionColor.value = store.getters['content/getContentCaptionColor'];
  canvas.value = document.getElementById('canvas');
});
// onUnmounted フック
onUnmounted(() => {
  document.getElementById('content-preview-full-screen-modal')?.remove();
});
</script>

<style scoped>
.ja-content-confirm {
  margin: auto;
}

.content-confirm-area {
  box-sizing: border-box;
  background-color: #fff;
  margin: auto;
  padding-top: 10vh;
  padding-top: 10dvh;
  padding-bottom: 25rem;
  z-index: 1;
}

.content-element-area {
  margin: 2rem auto;
  width: 90%;
}

.content-preview-button {
  margin: auto;
}

label {
  display: block;
  font-size: 1.3rem;
  margin-bottom: 1.5rem;
}

p {
  font-size: 1.5rem;
  margin: 0;
}

.template-title {
  color: black;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

.content-caption-area {
  position: relative;
  background-color: #fff;
  height: 20vh;
  width: 100%;
  z-index: 1;
}

.ja-content-caption-color-area {
  margin: 0;
  padding-left: 2.5%;
  width: 95%;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.ja-content-caption-color-cell {
  margin: 0.5rem;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid black;
}

.ja-content-caption-color-cell.isSelected {
  border: 5px solid green;
}

.text-area {
  position: absolute;
  height: 100%;
  width: 100%;
  padding: 1rem;
  border: solid #aaaaaa;
  font-size: 2rem;
  resize: none;
  box-sizing: border-box;
}

.edit-caption-label {
  display: none;
}

.template-description {
  color: black;
  font-size: 1.3rem;
  line-height: 1.5rem;
  margin: 0;
  width: 100%;
  white-space: pre-line;
}

.template-text-area {
  text-align: left;
}

.material-count,
.caption-count {
  color: #aaaaaa;
  text-align: right;
}

.preview-button {
  font-size: 1.5rem;
  background: #fff;
  border: solid 1px #cccccc;
  color: #222222;
}

.preview-description {
  font-size: 1.4rem;
  margin-top: 10px;
  text-align: center;
}

.modal-wrapper {
  position: relative;
  height: 100vh;
  height: 100dvh;
}

.modal-close-mask {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: inherit;
  border: none;
}

.modal-wrapper .preview-area {
  text-align: left;
}

.modal-close-button-area {
  height: 4rem;
  position: absolute;
  right: 3%;
  top: 3%;
  width: 4rem;
}

.modal-close-button,
.modal-close-button:active {
  align-items: center;
  background-color: #222222;
  border-radius: 50%;
  display: flex;
  height: 100%;
  justify-content: center;
  padding: 0;
  width: 100%;
  z-index: 1;
}

.modal-close-icon {
  font-size: 2.5rem;
  margin: auto;
}

.common-footer {
  height: 10rem;
  flex-direction: column;
}

.footer-button-area {
  height: 100%;
  width: 90%;
  padding-bottom: 1rem;
}

.footer-back-button {
  background: #fff;
  border: solid 1px #cccccc;
  color: #222222;
  margin: auto;
  padding: 1rem 0;
  text-align: center;
  width: 25%;
}

.footer-upload-button {
  box-shadow: 0rem 0.2rem #21963b;
  color: #fff;
  margin: auto;
  padding: 1rem 0;
  text-align: center;
  width: 65%;
}

.footer-back-button:hover,
.footer-upload-button:hover {
  opacity: 0.8;
}

@media screen and (min-width: 840px) {
  .content-element-area {
    width: 100%;
  }
}

.canvas {
  visibility: hidden;
}
</style>
